import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  PLATFORM_ID,
  Renderer2,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';

import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { Menu, MenuModule } from 'primeng/menu';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { ButtonComponent } from '../button/button.component';
import { BreadcrumbItem } from '../navbar/navbar.interface';

const MOBILE_BREAKPOINT = 768;

@Component({
  selector: 'nbx-breadcrumb',
  standalone: true,
  imports: [ButtonComponent, BreadcrumbModule, MenuModule, RouterLink, TranslocoPipe],
  templateUrl: './breadcrumb.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class BreadcrumbComponent {
  private readonly _transloco = inject(TranslocoService);
  private readonly _router = inject(Router);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _renderer = inject(Renderer2);
  private readonly _destroyRef = inject(DestroyRef);

  // Emits the active language's catalog once it has loaded, so a runtime switch re-labels the trail.
  private readonly _translation = toSignal(this._transloco.selectTranslation());
  private static readonly _KEY_RE = /^[A-Z0-9_.-]+$/;
  private readonly _primeItems = computed<MenuItem[]>(() => {
    this._translation();
    return this.items().map(item => ({
      label: BreadcrumbComponent._KEY_RE.test(item.label)
        ? this._transloco.translate(item.label)
        : item.label,
      routerLink: item.route ?? undefined,
      icon: item.icon,
      target: item.route ? '_self' : undefined,
    }));
  });

  readonly isMobile = signal(false);
  readonly items = input<BreadcrumbItem[]>([]);
  readonly menu = viewChild<Menu>('menu');

  readonly displayItems = computed<MenuItem[]>(() => {
    const raw = this._primeItems();
    const items = raw.map((item, i) => (i === raw.length - 1 ? { ...item, id: '__last__' } : item));
    if (!this.isMobile() || items.length <= 1) {
      return items;
    }
    const last = items[items.length - 1];
    return [{ id: '__ellipsis__', label: '...' }, last];
  });

  readonly mobileMenuItems = computed<MenuItem[]>(() =>
    this._primeItems()
      .slice(0, -1)
      .map(item => ({
        ...item,
        command: () => {
          if (item.routerLink) {
            this._router.navigate([item.routerLink as string]);
          }
        },
      }))
  );

  constructor() {
    if (isPlatformBrowser(this._platformId)) {
      this._updateIsMobile();
      const unlisten = this._renderer.listen('window', 'resize', () => this._updateIsMobile());
      this._destroyRef.onDestroy(unlisten);
    }
  }

  public toggleMenu(event: MouseEvent | KeyboardEvent | Event): void {
    this.menu()?.toggle(event);
  }

  private _updateIsMobile(): void {
    this.isMobile.set(window.innerWidth < MOBILE_BREAKPOINT);
  }
}
